import i18next from 'i18next';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MESSAGE_TYPES, ReceiveMessageType } from 'src/@types/message';
import useWebViewCommunication from 'src/hooks/useWebViewCommunication';
import { useStores } from 'src/models';
import { PATH_PAGE } from 'src/routes/paths';
import { settingsApi } from 'src/services';
import { isNativeApp } from 'src/utils/environment';

type NavigateTarget = 'mission' | 'missionHistory' | 'nicojini' | 'diary' | 'craving';

const NAVIGATE_TARGET_MAP: Record<NavigateTarget, string> = {
  mission: PATH_PAGE.mission.root,
  missionHistory: PATH_PAGE.mission.history,
  nicojini: PATH_PAGE.drJin.root,
  diary: PATH_PAGE.calendar.root,
  craving: PATH_PAGE.craving.root,
};

function AppMessageListener() {
  const { authStore } = useStores();
  const navigate = useNavigate();
  // handle message from RN
  const handleRNMessage = useCallback(
    (message: ReceiveMessageType) => {
      // AUTH_VERIFICATION 응답은 다른 핸들러에서 처리
      if ('resultCode' in message) {
        return;
      }

      switch (message.type) {
        case MESSAGE_TYPES.DEVICE_TOKEN: {
          const token = message.payload.deviceToken;
          settingsApi.saveDeviceToken(token);
          break;
        }
        case MESSAGE_TYPES.NAVIGATE: {
          const target = message.payload.target;
          const routePath = NAVIGATE_TARGET_MAP[target];
          if (routePath) {
            navigate(routePath);
          }
          break;
        }
      }
    },
    [navigate],
  );

  const { sendMessage } = useWebViewCommunication(handleRNMessage);

  useEffect(() => {
    if (authStore.user) {
      if (!authStore.isSaved) {
        sendMessage({ type: MESSAGE_TYPES.DEVICE_TOKEN });
        authStore.setProp('isSaved', true);
      }
    }
  }, [authStore, sendMessage]);

  // LANGUAGE_CHANGE 메시지 전송 (Web -> App)
  // 마운트 시 현재 언어 전송 + 언어 변경 이벤트 리스닝
  useEffect(() => {
    if (!isNativeApp()) return;

    // 마운트 시 현재 언어 전송
    sendMessage({
      type: MESSAGE_TYPES.LANGUAGE_CHANGE,
      payload: { language: i18next.language },
    });

    const handleLanguageChanged = (lng: string) => {
      sendMessage({
        type: MESSAGE_TYPES.LANGUAGE_CHANGE,
        payload: { language: lng },
      });
    };

    i18next.on('languageChanged', handleLanguageChanged);
    return () => {
      i18next.off('languageChanged', handleLanguageChanged);
    };
  }, [sendMessage]);

  return null;
}

export default AppMessageListener;
